import React from 'react';
import useCountdown from '../hooks/useCountdown';
import { useGetCurrentEpoch, useGetRound } from '../context/PredictionsContext';
import { motion } from 'framer-motion';

const TimeUnit = ({ value, label, showSeparator }) => {
  const isLow = value < 10;
  return (
    <>
      <motion.div
        className="flex flex-col items-center bg-[#1a1a1a] px-3 py-1 rounded-lg"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        key={value}
      >
        <span className={`text-xl font-bold ${isLow && label === "sec" || label === "min" && value === 0 ? 'text-red-500' : 'text-white'}`}>
          {value.toString().padStart(2, '0')}
        </span>
        <span className="text-xs text-gray-400 uppercase">{label}</span>
      </motion.div>
      {showSeparator && (
        <span className="text-lg text-[#7900ff] font-bold mx-1">:</span>
      )}
    </>
  );
};

/**
 * CountdownTimer that uses contract round data from context
 * @param {"lock"|"close"} type - Which timestamp to use
 * @param {Function} onComplete - Called when countdown finishes
 */
const CountdownTimer = ({ status, type = "close", onComplete }) => {

  const currentEpoch = useGetCurrentEpoch();
  const round = useGetRound(currentEpoch);

  let targetTimestamp = null;

  // Valid status and valid type and round exists
  const isValidStatus = status === "live" || status === "next" || status === "later";
  const isValidType = type === "lock" || type === "close";

  if (isValidStatus && isValidType && round) {
    if (status === "live") {
      targetTimestamp = Number(round.lockTimestamp) * 1000;
    }
    else if (status === "next") {
      const intervalInSeconds = 0;
      if (type === "lock" && round.lockTimestamp) {
        targetTimestamp = (Number(round.lockTimestamp) + intervalInSeconds) * 1000;
      } else if (type === "close" && round.closeTimestamp) {
        targetTimestamp = (Number(round.closeTimestamp) + intervalInSeconds) * 1000;
      }
    }
    else if (status === "later") {

      const intervalInSeconds = 300;
      targetTimestamp = (Number(round.lockTimestamp) + (intervalInSeconds)) * 1000;

    }

  }

  const { days, hours, minutes, seconds } = useCountdown(targetTimestamp, onComplete);

  // Early returns based on validation
  if (!isValidStatus) {
    return null; // Only show countdown for live or next rounds
  }

  if (!isValidType) {
    console.error("Invalid type provided to CountdownTimer. Use 'lock' or 'close'.");
    return null;
  }

  if (!targetTimestamp) {
    return (
      <div className="flex items-center justify-center p-2 rounded-xl bg-black/20 backdrop-blur-sm">
        <span className="text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-2 rounded-xl bg-black/20 backdrop-blur-sm">
      {days > 0 && (
        <TimeUnit value={days} label="days" showSeparator={true} />
      )}
      {/* Uncomment to show hours */}
      {/* <TimeUnit value={hours} label="hrs" showSeparator={true} /> */}
      <TimeUnit value={minutes} label="min" showSeparator={true} />
      <TimeUnit value={seconds} label="sec" showSeparator={false} />
    </div>
  );
};

export default CountdownTimer;